import {gql} from '@apollo/client';
import {DocumentNode} from 'graphql';
import React from 'react';

import {ANY_ARCHIVED_PURPOSE_STRING, NOT_ARCHIVED_PURPOSE_STRING} from 'archive/ArchiveConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {accessibleObjs} from 'graphql-client/InaccessibleInfo';
import {Filter, FilterKey, keyToEmptyFilter, Operator, SearchContext, SortKey} from 'search/SearchConstants';
import ItemSearchInput, {Item} from 'ui-library/attach-dropdown/ItemSearchInput';
import {ChipSize} from 'ui-library/chips/Chip';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import {searchPost} from 'ui-library/inputs/ChipSearchInput';
import {ElementKeyboardEvent} from 'util/Types';

import {ConnectionListingGraphQL} from '../../../graphql-fragments/instruments/__generated/ConnectionListingGraphQL';
import {
  ConnectionSearchInputQuery,
  ConnectionSearchInputQueryVariables,
} from './__generated/ConnectionSearchInputQuery';

const SEARCH_URL = '/1/api/connections:search';

type ConnectionSummaryItem = ConnectionListingGraphQL;

export interface ConnectionSearchInputProps {
  autoFocus?: boolean;
  chipSize?: ChipSize;
  className?: string;
  initialQuery?: string;
  onBlur?: () => void;
  onChange: (item: Array<ConnectionSummaryItem>) => void;
  onKeyDown?: (e: ElementKeyboardEvent) => void;
  selectedItems: Array<ConnectionSummaryItem | Item>;
  includeArchived?: boolean;
  shouldAllowMultiple?: boolean;
  hideSelectedResults?: boolean;
  context?: SearchContext;
  disabled?: boolean;
  isReadOnly?: boolean;
  extraFilters?: Array<Filter>;
}

export default function ConnectionSearchInput(props: ConnectionSearchInputProps): JSX.Element {
  const archivePurposes = props.includeArchived
    ? [ANY_ARCHIVED_PURPOSE_STRING, NOT_ARCHIVED_PURPOSE_STRING]
    : [NOT_ARCHIVED_PURPOSE_STRING];
  const archivePurposeFilter: Filter = {
    key: FilterKey.ARCHIVE_PURPOSES,
    operator: Operator.IS_ONE_OF,
    value: archivePurposes,
  };
  const filters = [
    ...(props.extraFilters ?? []),
    archivePurposeFilter,
    {...keyToEmptyFilter(FilterKey.TYPES), value: [ModelTypeConstants.CONNECTION]},
  ];
  return (
    <ItemSearchInput<ConnectionSummaryItem | Item>
      autoFocus={props.autoFocus}
      chipSize={props.chipSize}
      className={props.className}
      doSearch={searchPost}
      fetchParams={{
        filters,
        offset: 0,
        sorts: [{key: SortKey.SORT_RELEVANCE_KEY, reverse: false}],
        context: props.context,
      }}
      initialQuery={props.initialQuery}
      itemKey="connections"
      onBlur={props.onBlur}
      onChange={props.onChange}
      onKeyDown={props.onKeyDown}
      placeholder="Search for connections"
      searchURL={SEARCH_URL}
      selectedItems={props.selectedItems}
      shouldAllowMultiple={props.shouldAllowMultiple}
      hideSelectedResults={props.hideSelectedResults}
      disabled={props.disabled}
      isReadOnly={props.isReadOnly}
    />
  );
}

type ConnectionSearchInputWithItemsProps = {onChange: (newItems: Array<Item>) => void} & Omit<
  ConnectionSearchInputProps,
  'onChange'
>;

export function ConnectionSearchInputWithItems(props: ConnectionSearchInputWithItemsProps): JSX.Element {
  return <ConnectionSearchInput {...props} />;
}

export const CONNECTION_SEARCH_INPUT_QUERY: DocumentNode = gql`
  query ConnectionSearchInputQuery($ids: [StrictString!]!) @skipAudit {
    connections(ids: $ids) {
      ... on ConnectionNode {
        ...ConnectionListingGraphQL
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('ConnectionListing').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export function QueryingConnectionSearchInput(
  props: Omit<ConnectionSearchInputProps, 'selectedItems'> & {connectionIds: Array<string>}
): JSX.Element {
  return (
    <BenchlingQuery<ConnectionSearchInputQuery, ConnectionSearchInputQueryVariables>
      query={CONNECTION_SEARCH_INPUT_QUERY}
      variables={{ids: props.connectionIds}}
      renderLoading={() => <CenteredHorizontalThrobber />}
    >
      {({connections}) => <ConnectionSearchInput {...props} selectedItems={accessibleObjs(connections)} />}
    </BenchlingQuery>
  );
}
